import { useState, useEffect } from "react";
import { apiGet } from "../lib/apiClient.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import {
  selectLiveForecastPointer,
  fetchForecastReportJoined,
  fetchResolutionReport,
  isExpectedResolutionMiss,
  resolutionMissReason,
} from "../lib/forecast/reportPointer.js";
import { reportFailure } from "../lib/failureCounts.js";

// The caches are module singletons so they survive modal close and reopen. The 5-minute TTL
// is safe because a stored report row is immutable for the life of the run.
const REPORT_TTL_MS = 5 * 60 * 1000;
const _forecastReportCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });
const _resolutionReportCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });

/**
 * The returned value is derived on a key match (`state.key === key ? value : null`), so a
 * fetch resolved for a superseded key never leaks into the current render, and the effect
 * never calls setState synchronously to reset.
 *
 * `settled` separates "the read has not answered yet" from "the read answered with nothing",
 * which a null `value` conflates. A renderer that shows a fallback for missing data must
 * wait for it, or the fallback flashes in before the fetch lands. It is true when no read is
 * outstanding: the key's fetch has resolved, or no fetch was ever going to run.
 *
 * @param {string|null} key
 * @param {boolean} enabled
 * @param {(key:string)=>Promise<*>} fetcher
 * @returns {{value: *, settled: boolean}}
 */
function useKeyedReadThrough(key, enabled, fetcher) {
  const [state, setState] = useState({ key: null, value: null });
  useEffect(() => {
    if (!enabled || !key) return undefined;
    let cancelled = false;
    fetcher(key)
      .then((v) => {
        if (!cancelled) setState({ key, value: v });
      })
      .catch(() => {
        if (!cancelled) setState({ key, value: null });
      });
    return () => {
      cancelled = true;
    };
    // fetcher closes over stable singletons (or a test-injected apiGet); key +
    // enabled are the only real inputs.
  }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps
  // `active` is deliberately the same `enabled && key` condition the effect guards
  // on, so "a read is outstanding" is decided by whatever decided to fetch. Keep the
  // two in step if either changes.
  const active = !!(enabled && key);
  const matched = active && state.key === key;
  return { value: matched ? state.value : null, settled: !active || matched };
}

/**
 * Shares the module-private TTL cache `useReportPointer` reads, so the modal's read is then a
 * cache hit; this wrapper is how callers outside the hook reach that cache.
 *
 * Fire-and-forget: it never rejects, because a failed warm-up costs the user nothing — the
 * modal's own read is the real path. A lost warm-up is still a lost effect (P-08), so it is
 * logged and counted here as `resolution_prefetch.failed`: a rejected read, or a request the
 * resolver answered `!ok` (a network loss is status 0, a 5xx, a 401 handed back as an answer).
 * Quiet: a cache hit (no request), a 2xx — the resolver's 204 is the EXPECTED "no conclusive
 * report yet" miss, which `fetchResolutionReport` caches as null — and the `!ok` answers
 * `isExpectedResolutionMiss` names (503 `resolver_unavailable`, 403 `qkey_forbidden`, 504).
 * A 401 the client's default handler parks is deliberately uncounted: the sign-in redirect
 * replaces the page, so the request never answers and there is nothing to judge.
 *
 * @param {string|null|undefined} qkey
 * @param {{apiGet?: Function, cache?: object}} [deps]
 * @returns {Promise<void>}
 */
export async function prefetchResolutionReport(
  qkey,
  { apiGet: apiGetImpl = apiGet, cache = _resolutionReportCache } = {},
) {
  if (!qkey) return;
  // `fetchResolutionReport` returns null for a cache hit, a 204 and a `!ok` answer alike, and
  // `apiGet` never throws (a network loss resolves `{ok:false,status:0}`), so the answer is
  // observed on the way through rather than inferred from the result.
  let requested = false;
  let answer;
  const observed = async (...args) => {
    requested = true;
    answer = await apiGetImpl(...args);
    return answer;
  };
  try {
    // fetchResolutionReport short-circuits on a cache hit, so re-prefetching the same
    // contract inside the TTL costs no request.
    await fetchResolutionReport(qkey, { apiGet: observed, cache });
  } catch (err) {
    reportFailure("resolution_prefetch.failed", err);
    return;
  }
  if (!requested || answer?.ok) return;
  if (!isExpectedResolutionMiss(answer?.status, resolutionMissReason(answer?.body))) {
    reportFailure(
      "resolution_prefetch.failed",
      new Error(`/api/resolve/report answered status ${answer?.status}`),
    );
  }
}

/**
 * Reads through the same TTL cache as `useReportPointer`, so opening the modal afterwards is
 * a cache hit, or joins this read while it is still out. A latest pointer older than the live
 * contract moment is stale and yields null.
 *
 * @param {{reportPointers?: Array, liveMomentIso?: string|null, enabled?: boolean,
 *   apiGetImpl?: Function, forecastCache?: object}} args
 * @returns {object|null}
 */
export function useLiveForecastReport({
  reportPointers,
  liveMomentIso,
  enabled,
  apiGetImpl = apiGet,
  forecastCache = _forecastReportCache,
}) {
  const latest = enabled ? selectLiveForecastPointer(reportPointers, liveMomentIso) : null;
  const rowKey = latest ? latest.row_key : null;
  return useKeyedReadThrough(rowKey, !!enabled, (rk) =>
    fetchForecastReportJoined(rk, { apiGet: apiGetImpl, cache: forecastCache }),
  ).value;
}

/**
 * Each result is null until its fetch resolves and stays null on a 404 / 204 / miss, so the
 * caller falls back to the server_ctx render.
 *
 * The resolution fetch is gated separately on `resolutionEnabled`, so an unresolved forecast
 * never hits BigQuery via /resolve/report.
 *
 * @param {{
 *   reportPointers?: Array,
 *   contractKey?: string|null,
 *   liveMomentIso?: string|null,
 *   enabled?: boolean,
 *   resolutionEnabled?: boolean,
 *   apiGetImpl?: Function,
 *   forecastCache?: object, resolutionCache?: object
 * }} args
 * @returns {{ forecastSource: object|null, resolution: object|null, resolutionSettled: boolean }}
 *   `resolutionSettled` is false only while the resolution read is outstanding, so a null
 *   `resolution` can be told apart as "not answered yet" or "answered with nothing".
 */
export function useReportPointer({
  reportPointers,
  contractKey,
  liveMomentIso,
  enabled,
  resolutionEnabled,
  apiGetImpl = apiGet,
  forecastCache = _forecastReportCache,
  resolutionCache = _resolutionReportCache,
}) {
  // After a manual ↻ reforecast the live contract advances without writing a pointer, so the
  // latest pointer is a stale prior async fire; overlaying it would show that fire's report
  // over the newer live one and duplicate the newest scrub stop. See selectLiveForecastPointer.
  const latest = enabled ? selectLiveForecastPointer(reportPointers, liveMomentIso) : null;
  const rowKey = latest ? latest.row_key : null;

  const forecast = useKeyedReadThrough(rowKey, enabled, (rk) =>
    fetchForecastReportJoined(rk, { apiGet: apiGetImpl, cache: forecastCache }),
  );
  const resolutionRead = useKeyedReadThrough(contractKey || null, resolutionEnabled, (qk) =>
    fetchResolutionReport(qk, { apiGet: apiGetImpl, cache: resolutionCache }),
  );

  return {
    forecastSource: forecast.value,
    resolution: resolutionRead.value,
    resolutionSettled: resolutionRead.settled,
    // The live report row being overlaid (null when there is none) — its version identity.
    rowKey,
  };
}
